import { useCallback, useEffect, useRef, useState, type MutableRefObject, type RefObject } from 'react';
import type { CharacterPreviewCommand, PickedTarget } from '@sorrem-client/game/render-config';
import type { RenderWorkerSession } from '@sorrem-client/game/render-manager';

/** Dragging this far across the character turns it all the way around. */
const PIXELS_PER_TURN = 600;
/** Negative: dragging right swings the character's front to the right, as if turning it on a stand. */
const TURN_RADIANS_PER_PIXEL = -(Math.PI * 2) / PIXELS_PER_TURN;

interface TurnDrag {
    readonly pointerId: number;
    /** The `pointer-down` pick this press waits on. */
    readonly requestId: string;
    lastX: number;
    isTurning: boolean;
}

export interface CharacterTurnDrag {
    /** Whether a drag is turning the character now. */
    readonly isTurning: boolean;
    /** For the render session's `onPickResult`: a press that picked the character starts turning it. */
    readonly handlePickResult: (requestId: string, target: PickedTarget | null) => void;
}

/**
 * Dragging the character turns it left and right (#270). A press on the
 * canvas asks the worker what is under it (`pointer-down`), and only a press
 * on the character turns it, so the rest of the room stays free for its own
 * clicks (#196). Moves made before the answer still count, from where the
 * press began. Moves and the release are read on the window, so the drag
 * carries on past the character and ends wherever the button comes up.
 */
export function useCharacterTurnDrag(
    containerRef: RefObject<HTMLElement>,
    sessionRef: MutableRefObject<RenderWorkerSession<CharacterPreviewCommand> | null>,
    enabled: boolean,
    animationsEnabledRef: MutableRefObject<boolean>,
): CharacterTurnDrag {
    const [isTurning, setIsTurning] = useState(false);
    const dragRef = useRef<TurnDrag | null>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!enabled || !container) return;

        const onPointerDown = (event: PointerEvent): void => {
            const session = sessionRef.current;
            // Only a press on the picture: a control over it keeps its own clicks.
            if (!session || event.button !== 0 || !(event.target instanceof HTMLCanvasElement)) return;
            const rect = container.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) return;
            const requestId = crypto.randomUUID();
            dragRef.current = { pointerId: event.pointerId, requestId, lastX: event.clientX, isTurning: false };
            // The worker picks in drawing-buffer pixels.
            session.sendCommand({
                type: 'pointer-down',
                position: {
                    x: (event.clientX - rect.left) * (session.size.width / rect.width),
                    y: (event.clientY - rect.top) * (session.size.height / rect.height),
                },
                requestId,
            });
        };

        // A press on the picture never starts a text selection, which a drag across the panels would otherwise make.
        const onMouseDown = (event: MouseEvent): void => {
            if (event.button === 0 && event.target instanceof HTMLCanvasElement) event.preventDefault();
        };

        const onPointerMove = (event: PointerEvent): void => {
            const drag = dragRef.current;
            if (!drag || !drag.isTurning || event.pointerId !== drag.pointerId) return;
            const deltaX = event.clientX - drag.lastX;
            drag.lastX = event.clientX;
            if (deltaX === 0) return;
            sessionRef.current?.sendCommand({ type: 'turn-preview-character', radians: deltaX * TURN_RADIANS_PER_PIXEL });
            // A still frame (animations off) only repaints when asked.
            if (!animationsEnabledRef.current) {
                sessionRef.current?.sendCommand({ type: 'render-frame' });
            }
        };

        const onPointerUp = (event: PointerEvent): void => {
            if (dragRef.current?.pointerId !== event.pointerId) return;
            dragRef.current = null;
            setIsTurning(false);
        };

        container.addEventListener('pointerdown', onPointerDown);
        container.addEventListener('mousedown', onMouseDown);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);

        return (): void => {
            container.removeEventListener('pointerdown', onPointerDown);
            container.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
            dragRef.current = null;
            setIsTurning(false);
        };
    }, [containerRef, sessionRef, enabled, animationsEnabledRef]);

    const handlePickResult = useCallback((requestId: string, target: PickedTarget | null): void => {
        const drag = dragRef.current;
        // A pick for a press already let go, or for one that missed the character, turns nothing.
        if (!drag || drag.requestId !== requestId) return;
        if (target?.kind !== 'entity') {
            dragRef.current = null;
            return;
        }
        drag.isTurning = true;
        setIsTurning(true);
    }, []);

    return { isTurning, handlePickResult };
}
